
import { HomeLink } from "@/components/HomeLink";
import { AuthForm } from "@/components/AuthForm";
import { GameLogo } from "@/components/GameLogo";
import { useSearchParams, Navigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useEffect, useState } from "react";
import { Loader2, AlertCircle } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";

const Auth = () => {
  const [searchParams] = useSearchParams();
  const mode = searchParams.get('mode');
  const errorCode = searchParams.get('error_code');
  const errorDescription = searchParams.get('error_description');
  const [verifyingPasswordReset, setVerifyingPasswordReset] = useState(false);
  const [resetVerified, setResetVerified] = useState(false);
  
  // Check if this is a password reset with token
  useEffect(() => {
    const checkResetToken = async () => {
      if (mode === 'update-password') {
        setVerifyingPasswordReset(true);
        // The token is automatically handled by Supabase client
        try {
          // Verify the session exists after the reset password flow
          const { data: { session } } = await supabase.auth.getSession();
          if (session) {
            console.log("User is authenticated after password reset flow");
            setResetVerified(true);
          } else {
            console.log("No session found during password reset flow");
          }
        } catch (error) {
          console.error("Error verifying reset token:", error);
        } finally {
          setVerifyingPasswordReset(false);
        }
      }
    };
    
    checkResetToken();
  }, [mode]);
  
  // Skip auth check when updating password
  const skipAuthCheck = mode === 'update-password';
  
  // Check if user is already authenticated
  const { data, isLoading } = useQuery({
    queryKey: ['session'],
    queryFn: async () => {
      const { data } = await supabase.auth.getSession();
      return data.session;
    },
    // Skip this check when updating password with token
    enabled: !skipAuthCheck,
  });

  // If authenticated and not in password update mode, redirect to home
  if (!skipAuthCheck && !isLoading && data) {
    return <Navigate to="/" replace />;
  }
  
  // Show loading state while verifying password reset token
  if (verifyingPasswordReset) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gamePurple to-gameSlate flex flex-col items-center justify-center p-4">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-12 w-12 animate-spin text-gameGold" />
          <p className="text-gameGold text-xl">Verifying your password reset request...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-gamePurple to-gameSlate flex flex-col items-center justify-center p-4 relative">
      <HomeLink />
      <div className="w-full max-w-md mx-auto">
        <GameLogo className="mb-8" />
        
        {errorCode === 'otp_expired' && (
          <Alert variant="destructive" className="mb-6">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Password Reset Link Expired</AlertTitle>
            <AlertDescription>
              The password reset link you used has expired or is invalid. Please request a new password reset link using the "Forgot Password" option below.
            </AlertDescription>
          </Alert>
        )}
        
        <AuthForm 
          initialMode={mode === 'signup'} 
          isPasswordReset={resetVerified}
          initialForgotPassword={errorCode === 'otp_expired'} 
        />
      </div>
    </div>
  );
};

export default Auth;
